import { CompactDealStageResponse, DealId, DealResponse, DealStatus } from '@business/entities/deal';
import { FormTypes } from '@business/entities/form';
import { Button } from '@core/components/Button';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { ModalContent, ModalDrawer, ModalFooter, ModalHeader } from '@core/components/Modal';
import { useModalInstance } from '@core/components/ModalStack';
import { PCType, usePageContext } from '@core/components/PageContext';
import { useBloc } from '@core/utils/bloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { css } from '@emotion/react';
import { Tabs, TabsMapping, WithTooltip } from '@modules/common';
import { OrgFiles } from '@modules/common/OrgFiles';
import { openAddEditDealModal } from '@modules/deals/AddEditDealModal';
import React, { Fragment, memo, useCallback, useMemo, useState, useEffect } from 'react';
import { Card, CardBody, Col, Progress, Row, UncontrolledTooltip, Badge } from 'reactstrap';
import { tap } from 'rxjs/operators';
import { DealDetails } from '.';
import { openDealLostModal } from './DealLostModal';
import { DealOverlayBloc } from './DealOverlayBloc';
import { DealCommunicationTab } from './Tabs/DealCommunicationTab';
import { Hotness } from '@modules/common/Hotness';
import { OrgService, CommonService, HTMLService } from '@business/services';
import { useHistory, useQueryStringParam } from '@core/router';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import ProposalsPage from '@modules/organizations/OrganizationPage/ProposalsPage';
import { PermissionService } from '@business/services/permission_service';
import { isGCLabelActive } from '@core/utils/hooks/misc/gcLabel';
import { LabelNameEnum } from '@business/entities/label';
import { BillingCard } from '@modules/organizations/OrganizationPage/Components/Billing/Billing';
import { Session } from '@modules/auth/session';
import { OrganizationPageBloc } from '@modules/organizations/OrganizationPage/OrganizationPageBloc';
import { OrgId, OrgResponse } from '@business/entities/organization';
import { TextBlock } from '@modules/common/TextBlock';
import { openSelectUserModal } from '@modules/common/SelectUserModal';
import { FormSelect } from '@core/components/form';
import { formControl } from '@core/utils/form';
import { GCModulesCalendar } from '@modules/common/GCModulesCalendar';
import { ObjectType } from '@business/entities/contact';
import { GCCalendarEventObjectType } from '@business/entities/gc_calendar';
import './index.scss';

const styles = {
  content: css`
    padding: 25px !important;
    background-color: var(--body-bg-color);
  `,

  title: css`
    .MuiTypography-root {
      height: 65px;
      align-items: center;
      position: relative;
      padding: 0 10px 0 20px;
    }
  `,
};

interface DealProgressProps {
  deal: DealResponse;
  stages: CompactDealStageResponse[];
}

const DealProgress = memo((props: DealProgressProps) => {
  const { deal, stages } = props;

  return (
    <Card>
      <CardBody>
        <div id="custom-modal">
          <Row>
            {stages.map(stage => {
              return (
                <Col key={stage.id} className="px-1">
                  <Progress
                    animated
                    striped
                    id={`modal-1-${stage.id}`}
                    color="info"
                    value={stage.menuOrder <= deal.stage.menuOrder ? 100 : 0}
                    className="font-size-12"
                    style={{ height: '18px' }}
                  ></Progress>
                  <UncontrolledTooltip container="custom-modal" placement="top" target={`modal-1-${stage.id}`}>
                    <strong>{stage.title}</strong>
                    {!!stage.description && <p>{stage.description}</p>}
                  </UncontrolledTooltip>
                </Col>
              );
            })}
          </Row>
          <Row>
            <Col>
              <h6 className="mb-0 mt-2">
                {deal.board.name} - <a className="font-weight-normal"> {deal.stage.title} </a>
              </h6>
            </Col>
          </Row>
        </div>
      </CardBody>
    </Card>
  );
});

interface DealStatusesProps {
  deal: DealResponse;
  status: DealStatus;
  onDelete(): void;
  onChangeStatus(status: DealStatus): void;
}

const DealStatuses = memo((props: DealStatusesProps) => {
  const { deal, status, onDelete, onChangeStatus } = props;
  const canSelectWon = useMemo(() => {
    if (deal.proposals.length === 0) return true;
    return deal.proposals.every(p => p.status === 'accepted' || p.status === 'archived');
  }, [deal.proposals]);
  return (
    <div>
      {status !== DealStatus.won && canSelectWon && (
        <Button intent="success" onClick={() => onChangeStatus(DealStatus.won)}>
          <i className="bx bx-trophy font-size-16 mr-2"></i>
          Won
        </Button>
      )}
      {status !== DealStatus.won && !canSelectWon && (
        <WithTooltip tooltip="There is a proposal attached to this deal that is not accepted. Only deals with accepted proposals can be marked as won. Note: If you have multiple proposals attached to a deal as additional options and a customer approved one of them, you can mark the other proposals as Archived and you will be able to mark this deal as won.">
          <Button intent="light" disabled onClick={() => onChangeStatus(DealStatus.won)}>
            <i className="bx bx-trophy font-size-16 mr-2"></i>
            Won
          </Button>
        </WithTooltip>
      )}
      {status !== DealStatus.lost && (
        <Button extraClassNames="ml-2" intent="danger" onClick={() => onChangeStatus(DealStatus.lost)}>
          <i className="bx bx-unlink font-size-16 mr-2"></i>
          Lost
        </Button>
      )}
      {status !== DealStatus.inProgress && (
        <Button extraClassNames="ml-2" intent="light" onClick={() => onChangeStatus(DealStatus.reopen)}>
          <i className="bx bx-lock-open-alt font-size-16 mr-2"></i>
          Reopen
        </Button>
      )}

      {status === DealStatus.won && (
        <a href={`/organizations/${deal.organization.id}/billing`} className="btn btn-dark ml-2">
          Billing
        </a>
      )}
      {/* {status !== DealStatus.lost && status !== DealStatus.deleted && (
        <Button extraClassNames="ml-2" intent="danger" onClick={() => onChangeStatus(DealStatus.deleted)}>
          Remove
        </Button>
      )} */}

      <a className="ml-2" onClick={onDelete}>
        <i className="bx bx-trash font-size-16 text-danger"></i>
      </a>
    </div>
  );
});

export interface DealOverlayProps {
  dealId: DealId;
  orgId: OrgId;
  onNextDeal?(cDeal: DealId): DealId | undefined;
  onPrevDeal?(cDeal: DealId): DealId | undefined;
  onClose?(): void;
  switchRoute?: boolean;
}

export const DealOverlay = memo((props: DealOverlayProps) => {
  const { onNextDeal, onPrevDeal, switchRoute = true } = props;
  const [sDealId, setsDealId] = useState(props.dealId);
  const [sOrgId, setOrgId] = useState(props.orgId);
  const user = useObservable(Session.user$);
  const bloc = useBloc(DealOverlayBloc, sDealId, sOrgId);

  const orgBloc = useBloc(OrganizationPageBloc, props.orgId);
  const deal = useObservable(bloc.deal$) as DealResponse | null;
  const allStages = useObservable(bloc.stages$, []);
  const stages = useMemo(() => R.filter(allStages, s => s.board.id === (deal ? deal.board.id : '0')), [allStages, deal]);
  const modal = useModalInstance();
  const [showModal, setShowModal] = useState<boolean>(true);
  const history = useHistory();
  const hasNextDeal = useMemo(() => (onNextDeal ? (onNextDeal(sDealId) ? true : false) : false), [onNextDeal, sDealId]);
  const hasPrevDeal = useMemo(() => (onPrevDeal ? (onPrevDeal(sDealId) ? true : false) : false), [onPrevDeal, sDealId]);
  const filtersQuery = useQueryStringParam('q');
  const settings = useSettings();

  const stageControl = useMemo(() => formControl<string>({ placeholder: 'Deal Stage', initialValue: deal ? deal.stageId : undefined }), [deal]);
  const onStage = useCallback((stageId: number) => bloc.updateDealStage(deal!.id, R.toString(stageId)).subscribe(), [bloc, deal]);
  const stageItems = useMemo(() => {
    if (!deal) return [];
    return CommonService.selectItems(deal.board.stages, 'title', 'id');
  }, [deal]);

  usePageContext(() => {
    if (deal) {
      return { type: PCType.deal, object: deal };
    }
  }, [deal]);

  useEffect(() => {
    if (deal) {
      setOrgId(deal.organization.id);
    }
  }, [deal]);

  const onDelete = useCallback(
    (id: DealId) => {
      bloc
        .deleteDeal(id)
        .pipe(
          tap(() => {
            modal.close();
            props.onClose && props.onClose();
          }),
        )
        .subscribe();
    },
    [bloc, modal, props],
  );

  const onClose = useCallback(() => {
    if (switchRoute) history.push('/deals?q=' + filtersQuery);
    props.onClose && props.onClose();
  }, [switchRoute, history, filtersQuery, props]);

  const onStatus = useCallback(
    (id: DealId, status: DealStatus) => {
      if (status == DealStatus.lost) {
        openDealLostModal({ dealId: deal!.id, status, onDone: bloc.onReload, numberOfProposals: deal?.proposals.length });
      } else if (status == DealStatus.reopen) {
        bloc.updateDealStatus(id, DealStatus.inProgress).subscribe();
      } else {
        bloc.updateDealStatus(id, status).subscribe();
      }
    },
    [bloc, deal],
  );
  const loading = useLoadingState(bloc.loading);
  const scrollableDiv = 'dealOverlay';

  const showInternalScheduler = useMemo(() => CommonService.isGCInternalSchedularEnabled(settings), [settings]);

  const tabs: TabsMapping[] = useMemo(() => {
    const t = [];
    t.push({ title: 'Activities', component: DealCommunicationTab, props: { bloc, deal, scrollableDiv } });
    if (user && props.orgId && PermissionService.canSeeIt(user, 'billing')) {
      t.push({
        route: 'billing',
        title: 'Billing',
        leftIcon: 'bx bx-file font-size-14 mr-1',
        component: BillingCard,
        props: { bloc: orgBloc, orgId: props.orgId },
        visible: isGCLabelActive(LabelNameEnum.projects) || isGCLabelActive(LabelNameEnum.subs) || isGCLabelActive(LabelNameEnum.orders),
      });
    }

    if (settings?.accountSettings.proposals) {
      t.push({ title: 'Proposals', component: ProposalsPage, props: { orgId: deal?.organization.id || '0', moduleType: 'deal' } });
    }
    t.push({
      title: 'Files',
      component: OrgFiles,
      props: { orgId: deal?.organization.id || '0', type: FormTypes.organization, card: true, moduleId: deal ? deal.id : null, moduleType: 'deal' },
    });
    t.push({
      title: 'Calendar',
      component: GCModulesCalendar,
      props: {
        objectType: GCCalendarEventObjectType.Deal,
        objectId: deal ? deal.id : undefined,
        orgId: deal ? deal.organization.id : undefined,
        clientEmails: deal?.organization ? R.flatten(deal.organization.contacts.map(c => c.emails.map(e => e.value))) : [],
        objectName: deal ? deal.name : '',
        objectAddress: deal ? OrgService.getDefaultAddress(deal.organization as OrgResponse) : '',
      },
      visible: showInternalScheduler,
    });

    return t;
  }, [bloc, deal, orgBloc, props.orgId, settings?.accountSettings.proposals, showInternalScheduler, user]);

  const onAssignOwner = useCallback(() => {
    openSelectUserModal({
      onSelect: userId => bloc.updateDealAction(deal!.id, userId, 'owner_id'),
      title: 'Select Owner',
      button: 'Update',
      userId: deal!.owner.id,
    });
  }, [bloc, deal]);

  useEffect(() => {
    if (deal && deal.contact && !R.isEmpty(deal.contact.email)) {
      bloc.loadEvents(deal.contact.email);
    }
  }, [bloc, deal]);

  if (loading.loading || !deal) return <TopLoadingBar />;
  if (!showModal) return <Fragment></Fragment>;

  return (
    <ModalDrawer maxWidth="xl" onClose={onClose}>
      <ModalHeader customCss={styles.title} onClose={onClose}>
        <div id="custom-modal-hotness">
          <div className="d-flex flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="d-flex" style={{ flexDirection: 'row' }}>
              {deal.name}
              <Hotness container="custom-modal-hotness" style={{ marginLeft: 10 }} tooltip={OrgService.getLastActivity(deal)}>
                {deal.extra.hotness}
              </Hotness>

              <a className="ml-2">
                <i className="bx bx-edit font-size-16" onClick={() => openAddEditDealModal({ deal, boardId: deal.board?.id })}></i>
              </a>
              <Badge pill className={`badge-soft-info font-size-12 ml-4 px-2 py-1`}>
                {R.startCase(deal.status)}
              </Badge>
            </div>
            <div className="d-flex">
              <WithTooltip tooltip={HTMLService.toReact(deal.dealAgeToolip)} key={`deal-${deal.id}`}>
                <div>
                  <TextBlock title="Time on Board" classNameTitle="font-size-12" style={{ marginRight: 20 }}>
                    {deal.dealAge}
                  </TextBlock>
                </div>
              </WithTooltip>
              <TextBlock title="Owner" classNameTitle="font-size-12" style={{ marginRight: 20 }}>
                {CommonService.getFullName(deal.owner)}
                <a className="ml-2" onClick={onAssignOwner}>
                  <i className="bx bx-edit font-size-16"></i>
                </a>
              </TextBlock>

              <div style={{ width: 150, marginRight: 10 }}>
                <FormSelect control={stageControl} items={stageItems} withoutGroup onChange={onStage} />
              </div>

              <DealStatuses
                deal={deal}
                status={deal.status}
                onDelete={() => onDelete(deal.id)}
                onChangeStatus={status => onStatus(deal.id, status)}
              />
            </div>
          </div>
        </div>
      </ModalHeader>
      <ModalContent customCss={styles.content} id={scrollableDiv}>
        <DealDetails deal={deal} bloc={bloc} />
        <DealProgress deal={deal} stages={stages} />

        <div className="main_tabs">
          <Tabs pills tabs={tabs} />
        </div>
      </ModalContent>
      {(hasNextDeal || hasPrevDeal) && !!onNextDeal && !!onPrevDeal && (
        <ModalFooter>
          {hasPrevDeal && (
            <Button onClick={() => setsDealId(onPrevDeal(sDealId)!)}>
              <i className="bx bx-left-arrow-circle font-size-16 "></i> &nbsp; Prev Deal
            </Button>
          )}
          {hasNextDeal && (
            <Button onClick={() => setsDealId(onNextDeal(sDealId)!)}>
              <i className="bx bx-right-arrow-circle font-size-16 "></i> &nbsp; Next Deal
            </Button>
          )}
        </ModalFooter>
      )}
    </ModalDrawer>
  );
});
